import { ReactNode, useEffect, useRef } from 'react';
import * as Ariakit from '@ariakit/react';
import { AnimatePresence, motion } from 'motion/react';

type CollapsableProps = {
  collapsedContent?: ReactNode;
  expandedContent?: ReactNode;
  isExpanded: boolean;
};

export const Collapsable = ({ collapsedContent, expandedContent, isExpanded }: CollapsableProps) => {
  const store = Ariakit.useDisclosureStore({ defaultOpen: isExpanded });
  const isDisclosureExpanded = Ariakit.useStoreState(store, 'open');
  const motionOpenDivRef = useRef<HTMLDivElement | null>(null);
  const motionCloseDivRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Listen to the state change from the parent
    if (isExpanded !== isDisclosureExpanded) {
      store.setOpen(isExpanded);
    }
  }, [isExpanded, isDisclosureExpanded, store]);

  return (
    <Ariakit.DisclosureProvider store={store}>
      {!isDisclosureExpanded && (
        <AnimatePresence>
          <Ariakit.DisclosureContent
            alwaysVisible
            render={
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ bounce: 0, duration: 0.2 }}
                ref={motionCloseDivRef}
              />
            }
          >
            {collapsedContent}
          </Ariakit.DisclosureContent>
        </AnimatePresence>
      )}
      <AnimatePresence>
        {isDisclosureExpanded && (
          <Ariakit.DisclosureContent
            alwaysVisible
            render={
              <motion.div
                initial={{ opacity: 0, height: 25 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ bounce: 0, duration: 0.2 }}
                ref={motionOpenDivRef}
                onAnimationStart={() => {
                  if (motionOpenDivRef.current) {
                    motionOpenDivRef.current.style.overflow = 'hidden';
                  }
                }}
                onAnimationComplete={() => {
                  if (motionOpenDivRef.current) {
                    motionOpenDivRef.current.style.overflow = 'unset';
                  }
                }}
              />
            }
          >
            {expandedContent}
          </Ariakit.DisclosureContent>
        )}
      </AnimatePresence>
    </Ariakit.DisclosureProvider>
  );
};
